// CTAButton — primary + secondary variants for LandingV2.
// Primary: gradient bg (primary → secondary), glow on hover, scale 1.02.
// Secondary: outline-only, no fill, subtle bg on hover.
import React from "react";
import { motion } from "framer-motion";
import clsx from "clsx";

export default function CTAButton({
    children,
    variant = "primary",
    size = "lg",
    onClick,
    href,
    ariaLabel,
    testId,
    icon: Icon,
    className,
}) {
    const base = clsx(
        "inline-flex items-center justify-center gap-2 font-semibold rounded-xl",
        "transition-colors focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
        {
            "px-6 py-3 text-sm": size === "sm",
            "px-8 py-4 text-base": size === "lg",
        },
    );
    const styles = {
        primary: clsx(
            base,
            "text-black shadow-xcom-cta hover:shadow-xcom-cta-hover",
            "bg-white hover:bg-[#EDEDED]",
        ),
        secondary: clsx(
            base,
            "text-text-main border border-brand-border bg-transparent",
            "hover:bg-card-bg hover:border-brand-primary/60",
        ),
        ghost: clsx(
            base,
            "text-text-secondary hover:text-text-main bg-transparent",
        ),
    };

    const content = (
        <>
            {children}
            {Icon && <Icon className="w-4 h-4" aria-hidden />}
        </>
    );

    const MotionTag = motion(href ? "a" : "button");
    return (
        <MotionTag
            href={href}
            onClick={onClick}
            aria-label={ariaLabel}
            data-testid={testId}
            className={clsx(styles[variant], className)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
        >
            {content}
        </MotionTag>
    );
}
